// Shared helpers for the dashboard panels. Anything a role dashboard needs to
// format or label lives here so the four of them stay in step.

export const formatCount = (n) => {
  if (n === null || n === undefined) return null;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
};

/* Clients and vendors of type "Individual" carry no company name - fall back to
   their primary user (`primary_user` on clients, `primary_users` on vendors). */
export const entityName = (e) => {
  if (!e) return null;
  const company = e.company_name?.trim();
  if (company) return company;
  const person = e.primary_user ?? e.primary_users;
  const full = `${person?.first_name ?? ""} ${person?.last_name ?? ""}`.trim();
  return full || null;
};

export const fmtAmount = (value, currency) => {
  if (value === null || value === undefined || value === "") return "—";
  const code = currency?.currency?.code ?? currency?.code ?? "";
  const amount = Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${code} ${amount}`.trim();
};

export const fmtDate = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "—";

// Whole days from today to `value`, negative once the date has passed.
export const daysUntil = (value) => {
  if (!value) return null;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const target = new Date(value);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - startOfToday) / 86400000);
};

export const dueLabel = (days) => {
  if (days === null) return "—";
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `In ${days}d`;
};

export const greetingFor = (name) => {
  const hour = new Date().getHours();
  const part = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  return name ? `${part}, ${name}` : part;
};

export const displayNameOf = (user, fallback = "there") =>
  user?.username || user?.email?.split("@")[0] || fallback;

// Counts occurrences of `field` across rows, keeping the order given in `order`
// so a breakdown always reads in pipeline order rather than by size.
export const countBy = (rows, field, order) => {
  const tally = {};
  rows.forEach((row) => {
    const key = row[field];
    if (!key) return;
    tally[key] = (tally[key] ?? 0) + 1;
  });
  const keys = order
    ? order.filter((k) => tally[k])
    : Object.keys(tally).sort((a, b) => tally[b] - tally[a]);
  return keys.map((key) => ({ key, count: tally[key] }));
};

// Rows per calendar month, oldest first, for the last `months` months.
//
// The list arrives newest first, so every month after the oldest row we hold is
// complete. When the list was cut short by the row limit, that oldest month is
// the only partial one and is dropped rather than drawn as a short bar.
export const monthlyCounts = (rows, { field = "createdAt", months = 6, truncated = false } = {}) => {
  const bucket = (d) => `${d.getFullYear()}-${d.getMonth()}`;

  const tally = {};
  rows.forEach((row) => {
    if (!row[field]) return;
    const key = bucket(new Date(row[field]));
    tally[key] = (tally[key] ?? 0) + 1;
  });

  let cutoff = null;
  const oldestRow = rows[rows.length - 1];
  if (truncated && oldestRow?.[field]) {
    const oldest = new Date(oldestRow[field]);
    cutoff = new Date(oldest.getFullYear(), oldest.getMonth() + 1, 1);
  }

  const now = new Date();
  const points = [];
  for (let back = months - 1; back >= 0; back--) {
    const month = new Date(now.getFullYear(), now.getMonth() - back, 1);
    if (cutoff && month < cutoff) continue;
    points.push({
      key: bucket(month),
      label: month.toLocaleDateString(undefined, { month: "short" }),
      count: tally[bucket(month)] ?? 0,
    });
  }
  return points;
};

// A breakdown built from the rows we actually pulled: say so when the list was
// cut short rather than implying it covers everything.
export const scopeLabel = (shown, total, noun) => {
  if (total === null || total === undefined) return null; // still loading
  if (!total) return `No ${noun} yet`;
  if (shown >= total) return `All ${total} ${noun}`;
  return `Latest ${shown} of ${total} ${noun}`;
};
